/**
 * CARA (Cross-Account Relational Analysis) API Service
 *
 * Frontend API client for managing cross-account relationships.
 */

import axios from 'axios';
import { API_BASE_URL, attachAuthInterceptors } from './session';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

attachAuthInterceptors(api);

// ============================================================================
// Types
// ============================================================================

export interface FilterCondition {
  field: string;
  operator: string;
  value?: string | number | string[] | number[];
}

export interface FilterGroup {
  logic: 'AND' | 'OR';
  conditions: (FilterCondition | FilterGroup)[];
}

export interface SideMetric {
  field: string;
  function: string;
}

export interface SideQuery {
  filters?: FilterGroup;
  join_details?: boolean;
  detail_filters?: FilterGroup;
  time_anchor: string;
}

export interface DataLag {
  value: number;
  unit: 'days';
}

export interface SideADefinition {
  query: SideQuery;
  metric: SideMetric;
  aggregation: {
    granularity: string;
    data_lag?: DataLag;
  };
}

export interface SideBDefinition {
  query: SideQuery;
  metric: SideMetric;
  aggregation: {
    granularity: string;
    period_offset: number;
    data_lag?: DataLag;
  };
}

export interface BondDefinition {
  bond_type: string;
  direction: string;
  parameters: Record<string, unknown>;
  anomaly_sensitivity?: string;
}

export interface RelationshipDefinition {
  side_a: SideADefinition;
  side_b: SideBDefinition;
  bond: BondDefinition;
}

export interface RelationshipRecord {
  id: string;
  name: string;
  side_a_account_id: string;
  side_a_account_name: string;
  side_b_account_id: string;
  side_b_account_name: string;
  definition: RelationshipDefinition;
  validation_result: Record<string, unknown> | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface FieldOption {
  slug: string;
  label: string;
  mapping_level: string;
  data_type: string;
  unit?: string;
}

export interface FilterFieldOption extends FieldOption {
  distinct_values: string[];
}

export interface TimeAnchorOption {
  value: string;
  label: string;
}

export interface SchemaData {
  has_details: boolean;
  metric_fields: FieldOption[];
  filter_fields: FilterFieldOption[];
  time_anchors: TimeAnchorOption[];
}

// ============================================================================
// API Methods
// ============================================================================

export const caraApi = {
  listRelationships: async (status?: string): Promise<{ success: boolean; data: RelationshipRecord[]; count: number }> => {
    const params = status ? { status } : {};
    const response = await api.get('/api/relationships', { params });
    return response.data;
  },

  getRelationship: async (id: string): Promise<{ success: boolean; data: RelationshipRecord }> => {
    const response = await api.get(`/api/relationships/${id}`);
    return response.data;
  },

  createRelationship: async (data: {
    name: string;
    side_a_account_id: string;
    side_b_account_id: string;
    definition: RelationshipDefinition;
  }): Promise<{ success: boolean; data: RelationshipRecord }> => {
    const response = await api.post('/api/relationships', data);
    return response.data;
  },

  updateRelationship: async (
    id: string,
    data: { name?: string; definition?: RelationshipDefinition; status?: string }
  ): Promise<{ success: boolean; data: RelationshipRecord }> => {
    const response = await api.patch(`/api/relationships/${id}`, data);
    return response.data;
  },

  deleteRelationship: async (id: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/relationships/${id}`);
    return response.data;
  },

  validateRelationship: async (id: string): Promise<{ success: boolean; data: Record<string, unknown> }> => {
    const response = await api.post(`/api/relationships/${id}/validate`);
    return response.data;
  },

  getSchema: async (accountId: string): Promise<{ success: boolean; data: SchemaData }> => {
    const response = await api.get(`/api/relationships/schema/${accountId}`);
    return response.data;
  },
};
